import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ISaveTask, ITask, ITaskResult } from '../../../../../models/tasks.interface';
import { TasksService } from '../../../../../services/tasks/tasks.service';
import { catchError, combineLatest, filter, finalize, map, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { HttpErrorResponse } from '@angular/common/http';
import { CVANotificationService } from '../../../../../services/cva-notification/cva-notification.service';
import { NotificationClassEnum } from '../../../../../models/notification-class.enum';
import { CVA_CONSTANTS } from '../../../../../util/constants';
import { ConsultationRecommendationService } from '../../../../../services/consultation-recommendation/consultation-recommendation.service';

@Component({
  standalone: false,
  selector: 'cva-consultation-tasks',
  templateUrl: './consultation-tasks.component.html',
  styleUrl: './consultation-tasks.component.scss',

})
export class ConsultationTasksComponent implements OnInit, OnDestroy {
  @ViewChild('bottom') bottomRef!: ElementRef;
  @Input() patientId: string;
  @Input() practitionerId: string;

  newTask = {
    category: '',
    task: ''
  };

  editIndex: number | null = null;
  editTask = { category: '', task: '' };

  categories = [
    { id: 'Action', name: 'Action' },
    { id: 'Appointment', name: 'Appointment' },
    { id: 'Document', name: 'Document' },
    { id: 'Inbox', name: 'Inbox' },
    { id: 'Order', name: 'Order' }
  ];

  tasks: ITask[] = [];
  cvaUI_url = environment.cvaUI_url;

  showAddForm = false;
  isLoading = false;

  private readonly _unsubscribeAll = new Subject<void>();

  constructor(
    private readonly cvaNotificationService: CVANotificationService,
    private readonly tasksService: TasksService,
    private readonly recommendationService: ConsultationRecommendationService
  ) {

  }

  ngOnInit(): void {
    if (environment.RECOMMENDATIONS.TASKS) {
      this.listenToGenerateTasks();
    }
    this.tasksService.tasks$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(tasks => this.tasks = tasks);
  }

  toggleCheckbox(index: number) {
    this.tasksService.toggleItem(index);
  }

  startEditTask(index: number) {
    this.editIndex = index;
    this.editTask = {
      category: this.tasks[index].category,
      task: this.tasks[index].task
    };
  }

  deleteTask(index: number) {
    this.tasksService.deleteTasks(index);
  }

  addTask() {
    const trimmedTask = this.newTask.task?.trim();
    if (!this.newTask.category || !trimmedTask) {
      return;
    }

    this.tasksService.addTask({
      checked: false,
      category: this.newTask.category,
      task: this.newTask.task
    });

    this.newTask = {
      category: '',
      task: ''
    };
    this.showAddForm = false;

    setTimeout(() => this.scrollToBottom(), 100);

  }

  cancelEdit() {
    this.editIndex = null;
  }

  updateTask() {
    const trimmedTask = this.editTask.task?.trim() || '';
    if (this.editIndex !== null && trimmedTask) {
      this.tasksService.updateTask(
        this.editIndex,
        this.editTask
      )
      this.editIndex = null;
    }
  }

  get isSubmitDisabled(): boolean {
    return !this.tasks.some(item => item.checked && !item.isSaved);
  }

  trackByIndex(index: number): number {
    return index;
  }

  postChecked() {
    const filteredTasks = this.tasks
      .map((task, index) => ({ ...task, index }))
      .filter(task => task.checked && !task.isSaved)
      .map(({ category, task, index }) => ({ category, task, index: String(index) }));

    if (!filteredTasks.length) {
      return;
    }

    const payload: ISaveTask = {
      tasks: JSON.parse(JSON.stringify(filteredTasks)),
      patientId: this.patientId,
      practitionerId: this.practitionerId
    }

    this.tasksService.saveTasks(payload)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({
        next: (response: ITaskResult) => {

          response.success.forEach((id) => {
            const index = Number(id);
            if (this.tasks[index]) {
              this.tasks[index].isSaved = true;
              this.tasks[index].checked = false;
            }
          });

          response.error.forEach((id) => {
            const index = Number(id);
            if (this.tasks[index]) {
              this.tasks[index].checked = false;
            }
          });

          if (response.error.length === 0) {
            this.cvaNotificationService.showNotification(
              CVA_CONSTANTS.MESSAGE.TASK_ADDED_SUCCESSFULLY,
              NotificationClassEnum.SUCCESS
            );
          } else {
            this.cvaNotificationService.showNotification(
              `${response.error.length} ${CVA_CONSTANTS.MESSAGE.TASK_ADDED_ERR}`,
              NotificationClassEnum.ERROR
            );
          }

          this.tasksService.setTasks(JSON.parse(JSON.stringify(this.tasks)));
        },
        error: (errorResponse: HttpErrorResponse) => {
          let errorMessage: string = '';
          if (errorResponse.error?.error) {
            errorMessage = errorResponse.error.error.errorMessage;
          }
          this.cvaNotificationService.showNotification(
            errorMessage || CVA_CONSTANTS.MESSAGE.save_err_soap_notes,
            NotificationClassEnum.ERROR
          );
        }
      }))
  }

  listenToGenerateTasks() {
    this.recommendationService.generateNotesTrigger$
      .pipe(
        takeUntil(this._unsubscribeAll),
        // Get the current failed state when the trigger happens
        switchMap((payload) =>
          this.recommendationService.generateNotesFailed$.pipe(
            take(1),
            map((failed) => ({ failed, payload }))
          )
        ),
        // Skip API call if parent already failed
        filter(({ failed }) => !failed),
        filter(({ payload }) =>
          payload.isTranscriptionChanged ||
          this.tasks.length === 0
        ),
        // Proceed with the API call
        switchMap(({ payload }) => {
          this.isLoading = true;
          return this.tasksService.generateTasks(payload.transcript)
            .pipe(
              finalize(() => {
                this.isLoading = false;   // always runs (success, error, cancel)
              }),
              catchError((err) => {
                console.error('[Child] generateTasks() failed', err);
                this.tasksService.setTasks([], true);
                return of({ response: { tasks: [] } })
              })
            );
        }),
        // Wait until parent signals it's ready before using the response
        switchMap((res) =>
          combineLatest([
            this.recommendationService.generateNotesReady$,
            this.recommendationService.generateNotesFailed$
          ]).pipe(
            filter(([ready, failed]) => ready || failed),
            take(1),
            map(([ready]) => ready ? res : { response: { tasks: [] } })
          )
        )
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          let taskList = res.response.tasks || [];
          taskList = taskList.sort((task1, task2) => task1.category.localeCompare(task2.category));
          this.tasksService.setTasks(taskList, true);
        }
      });
  }

  clearAll() {
    this.tasksService.clearAll();
  }

  showAddItemForm() {
    this.showAddForm = true;
    setTimeout(() => this.scrollToBottom(), 100);
  }

  scrollToBottom() {
    try {
      this.bottomRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
    } catch {
      console.error("Error on scroll");
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
